import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { Security } from '@okta/okta-react';
import TabContainer from './TabContainer';
import NewDashboard from './NewDashboard';
import PerfDashboardPage from './PerfDashboardPage';
import SecureRoute from './components/SecureRoute';
import LoginCallback from './components/LoginCallback';
import RequestAccess from './components/RequestAccess';
import VersionDisplay from './VersionDisplay';
import oktaConfig from './config/oktaConfig';
import { models } from './ModelSelector';
import { authService } from './services/authService';
import ViewAsWidget from './debug/ViewAsWidget';
import ServerStatusOverlay from './components/ServerStatusOverlay';
import DevEnvBanner from './components/DevEnvBanner';
import AdminAppsPage from './admin/AdminAppsPage';
import AdminEstimationConfig from './admin/AdminEstimationConfig';
import AdminNetCheck from './admin/AdminNetCheck';
import AdminRepoHealth from './admin/AdminRepoHealth';
import AdminCredentials from './admin/AdminCredentials';
import AdminTesterLab from './admin/AdminTesterLab';
import AdminSalesforceSync from './admin/AdminSalesforceSync';
import AdminAwsLogsAccess from './admin/AdminAwsLogsAccess';
import AdminDeployActivity from './admin/AdminDeployActivity';
import AdminSalesforceApiAccess from './admin/AdminSalesforceApiAccess';
import AdminSms from './admin/AdminSms';
import AdminAccessControl from './admin/AdminAccessControl';
import LiveTesterOverlay from './tester-widget/LiveTesterOverlay';

// Initialize Okta Auth with error handling
let oktaAuth;
try {
  oktaAuth = new OktaAuth(oktaConfig);
  
  // Initialize auth service with oktaAuth instance
  authService.setOktaAuth(oktaAuth);
  
  // Add error handling for token manager events
  oktaAuth.tokenManager.on('error', (error) => {
    console.error('Token Manager Error:', error);
    // Handle refresh token errors specifically
    if (error.message && error.message.includes('refresh')) {
      console.warn('Refresh token error - clearing tokens');
      oktaAuth.tokenManager.clear().catch(console.error);
    }
  });
  
  console.log('Okta Auth initialized successfully');
} catch (error) {
  console.error('Failed to initialize Okta Auth:', error);
  // Create a mock oktaAuth object to prevent app crashes
  oktaAuth = {
    handleLoginRedirect: () => Promise.reject(new Error('Okta not initialized')),
    // Must reject (not just log) so callers awaiting this promise — like
    // SecureRoute's auto-redirect effect — see a real failure and fall back
    // to the branded login page instead of treating the no-op as success and
    // leaving the user on an endless spinner.
    signInWithRedirect: () => {
      console.error('Okta not initialized');
      return Promise.reject(new Error('Okta not initialized'));
    },
    tokenManager: {
      on: () => {},
      get: () => Promise.resolve(null),
      clear: () => Promise.resolve()
    }
  };
}

const App = () => {
  // Check for deep link query params (e.g. ?tab=pullrequest&repo=owner/repo)
  const urlParams = new URLSearchParams(window.location.search);
  const initialTab = urlParams.get('tab');

  const [isStarted, setIsStarted] = useState(!!initialTab);
  const [selectedModel, setSelectedModel] = useState(() => {
    const savedModel = localStorage.getItem('selectedModel');
    return savedModel || (models && models.length > 0 ? models[0].id : 'gemini-2.5-flash');
  });

  useEffect(() => {
    localStorage.setItem('selectedModel', selectedModel);
  }, [selectedModel]);

  const handleStart = () => {
    setIsStarted(true);
  };

  const handleHide = () => {
    setIsStarted(false);
  };

  return (
    <Router>
      <SecurityShell>
        <Routes>
          <Route path="/login/callback" element={<LoginCallback />} />
          <Route path="/request-access" element={
            <SecureRoute>
              <RequestAccess />
            </SecureRoute>
          } />
          <Route path="/admin/apps" element={
            <SecureRoute>
              <AdminAppsPage />
            </SecureRoute>
          } />
          <Route path="/admin/deploy-activity" element={
            <SecureRoute>
              <AdminDeployActivity />
            </SecureRoute>
          } />
          <Route path="/admin/estimation" element={
            <SecureRoute>
              <AdminEstimationConfig />
            </SecureRoute>
          } />
          <Route path="/admin/netcheck" element={
            <SecureRoute>
              <AdminNetCheck />
            </SecureRoute>
          } />
          <Route path="/admin/repo-health" element={
            <SecureRoute>
              <AdminRepoHealth />
            </SecureRoute>
          } />
          <Route path="/admin/credentials" element={
            <SecureRoute>
              <AdminCredentials />
            </SecureRoute>
          } />
          <Route path="/admin/tester-lab" element={
            <SecureRoute>
              <AdminTesterLab />
            </SecureRoute>
          } />
          <Route path="/admin/sync" element={
            <SecureRoute>
              <AdminSalesforceSync />
            </SecureRoute>
          } />
          <Route path="/admin/aws-logs" element={
            <SecureRoute>
              <AdminAwsLogsAccess />
            </SecureRoute>
          } />
          <Route path="/admin/salesforce-api" element={
            <SecureRoute>
              <AdminSalesforceApiAccess />
            </SecureRoute>
          } />
          <Route path="/admin/sms" element={
            <SecureRoute>
              <AdminSms />
            </SecureRoute>
          } />
          <Route path="/admin/access-control" element={
            <SecureRoute>
              <AdminAccessControl />
            </SecureRoute>
          } />
          <Route path="/tester/live/:appName" element={
            <SecureRoute>
              <LiveTesterOverlay />
            </SecureRoute>
          } />
          <Route path="/perf-dashboard" element={
            <SecureRoute>
              <PerfDashboardPage />
            </SecureRoute>
          } />
          <Route path="/*" element={
            <SecureRoute>
              {isStarted ? (
                <TabContainer
                  onHide={handleHide} 
                  selectedModel={selectedModel}
                  initialTab={initialTab}
                />
              ) : (
                <NewDashboard
                  onStart={handleStart}
                  selectedModel={selectedModel}
                  setSelectedModel={setSelectedModel}
                />
              )}
            </SecureRoute>
          } />
        </Routes>
        <VersionDisplay />
        <ViewAsWidget />
        <ServerStatusOverlay />
        <DevEnvBanner />
      </SecurityShell>
    </Router>
  );
};

/**
 * Wraps Okta's <Security> so we can resolve `restoreOriginalUri` via
 * react-router's navigate() instead of window.history.replaceState(). The old
 * replaceState approach changed the URL bar but didn't tell React Router the
 * route had changed, so after Okta's PKCE callback the spinner page stayed
 * mounted and the user had to log in a second time before the app rendered.
 * Living inside <Router> is what gives us access to useNavigate().
 */
const SecurityShell = ({ children }) => {
  const navigate = useNavigate();
  const restoreOriginalUri = useCallback((_oktaAuth, originalUri) => {
    const safeUri = toRelativeUrl(originalUri || '/', window.location.origin);
    navigate(safeUri, { replace: true });
  }, [navigate]);
  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      {children}
    </Security>
  );
};

export default App;


